// Pure filter/sort helpers.
// Alias-friendly matcher (Prompt 2.1): each facet declares a
// `normalize(tag)` that maps a raw tag → a canonical facet value.  Brand
// uses `product.vendor` (case-insensitive).

import { PRICE_BOUNDS } from "./filterTaxonomy";
import { isInStock, isSoldOut } from "./preorder";

function productPrice(p) {
  const amt = p.priceRange?.minVariantPrice?.amount;
  const n = Number(amt);
  return Number.isFinite(n) ? n : null;
}

// Return the set of canonical values this product exposes for `dim`.
function extractValues(product, dim) {
  const out = new Set();
  if (dim.source === "brand") {
    // CANONICAL_SOURCE = product.vendor (100% populated across mice/
    // keyboards vendors in the Loadout catalog). Historically we also
    // merged in `brand:<slug>` tags, but that produced two buckets for
    // the same brand when a product carried BOTH signals (e.g. vendor
    // "WL Mouse" + tag `brand:wlmouse` yielded "WL MOUSE" + "WLMOUSE").
    // Vendor-only, lowercased + whitespace-collapsed, is the single key.
    const v = (product.vendor || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (v) out.add(v);
    return out;
  }
  if (dim.source === "tag" && typeof dim.normalize === "function") {
    for (const t of product.tags || []) {
      const s = String(t).toLowerCase();
      const v = dim.normalize(s);
      if (v) out.add(v);
    }
    return out;
  }
  return out;
}

// -------------------------------------------------------------
// applyFilters
// -------------------------------------------------------------
export function applyFilters(products, taxonomy, activeFilters) {
  return products.filter((p) => {
    for (const [key, dim] of Object.entries(taxonomy)) {
      const active = activeFilters[key];
      if (!active) continue;

      if (dim.type === "price") {
        const price = productPrice(p);
        if (price == null) continue;
        if (active.min != null && price < active.min) return false;
        if (active.max != null && price > active.max) return false;
        continue;
      }

      if (dim.type === "stock") {
        if (!(active instanceof Set) || active.size === 0) continue;
        const inStock = isInStock(p);
        const wantsIn = active.has("in-stock");
        const wantsPre = active.has("pre-order");
        const isPre = !inStock && !isSoldOut(p);
        if (wantsIn && wantsPre) { if (!inStock && !isPre) return false; continue; }
        if (wantsIn && !inStock) return false;
        if (wantsPre && !isPre) return false;
        continue;
      }

      if (!(active instanceof Set) || active.size === 0) continue;

      const vals = extractValues(p, dim);
      let matched = false;
      for (const v of vals) {
        if (active.has(v)) {
          matched = true;
          break;
        }
      }
      if (!matched) return false;
    }
    return true;
  });
}

// -------------------------------------------------------------
// buildFacets
// -------------------------------------------------------------
export function buildFacets(products, taxonomy, activeFilters) {
  const facets = {};
  for (const [key, dim] of Object.entries(taxonomy)) {
    const others = { ...activeFilters };
    delete others[key];
    const candidates = applyFilters(products, taxonomy, others);

    if (dim.type === "price") {
      let min = Infinity;
      let max = -Infinity;
      for (const p of candidates) {
        const v = productPrice(p);
        if (v == null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      facets[key] = {
        candidateCount: candidates.length,
        min: Number.isFinite(min) ? Math.floor(min) : PRICE_BOUNDS.min,
        max: Number.isFinite(max) ? Math.ceil(max) : PRICE_BOUNDS.max,
      };
      continue;
    }

    if (dim.type === "stock") {
      let inCnt = 0;
      let preCnt = 0;
      for (const p of candidates) {
        if (isInStock(p)) inCnt++;
        else if (!isSoldOut(p)) preCnt++;
      }
      facets[key] = {
        candidateCount: candidates.length,
        values: [
          { key: "in-stock", label: "In Stock", count: inCnt },
          { key: "pre-order", label: "Pre-Order", count: preCnt },
        ].filter((v) => v.count > 0),
      };
      continue;
    }

    const counts = new Map();
    for (const p of candidates) {
      const vs = extractValues(p, dim);
      for (const v of vs) {
        counts.set(v, (counts.get(v) || 0) + 1);
      }
    }

    // Declared value order first (with human labels via dim.labels[value]).
    // For declared values, ALWAYS include the row — even if count is 0 —
    // so the UI can render a row users can click (e.g. switches MX row,
    // switches Buttery row) that yields an empty grid.  When a facet has
    // a declared `values` list, we DO NOT surface non-declared tokens (a
    // product's unrelated tags shouldn't leak into every facet).  Only
    // facets without a declared `values` list (e.g. brand) hide empty rows
    // and surface every counted value.
    const declared = Array.isArray(dim.values) ? dim.values : null;
    const humanLabel = (v) => (dim.labels && dim.labels[v]) || v;
    const orderedValues = [];
    if (declared) {
      for (const v of declared) {
        orderedValues.push({ key: v, label: humanLabel(v), count: counts.get(v) || 0 });
      }
    } else {
      for (const [v, c] of counts.entries()) {
        orderedValues.push({ key: v, label: humanLabel(v), count: c });
      }
      orderedValues.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }

    facets[key] = { candidateCount: candidates.length, values: orderedValues };
  }
  return facets;
}

// -------------------------------------------------------------
// Sort
// -------------------------------------------------------------
export const SORT_OPTIONS = [
  { key: "featured", label: "Featured" },
  { key: "newest", label: "Newest first" },
  { key: "price-asc", label: "Price: low to high" },
  { key: "price-desc", label: "Price: high to low" },
  { key: "name-asc", label: "Name: A → Z" },
];

// Sold-out products always sink to the end, preserving the chosen order.
export function applySort(products, sortKey) {
  const sorted = sortProducts(products, sortKey);
  return [...sorted.filter((p) => !isSoldOut(p)), ...sorted.filter(isSoldOut)];
}

function sortProducts(products, sortKey) {
  const arr = [...products];
  switch (sortKey) {
    case "price-asc":
      return arr.sort((a, b) => (productPrice(a) ?? Infinity) - (productPrice(b) ?? Infinity));
    case "price-desc":
      return arr.sort((a, b) => (productPrice(b) ?? -Infinity) - (productPrice(a) ?? -Infinity));
    case "name-asc":
      return arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    case "newest":
      return arr.reverse();
    case "featured":
    default:
      return arr;
  }
}

// -------------------------------------------------------------
// URL sync
// -------------------------------------------------------------
export function readFiltersFromUrl(searchParams, taxonomy) {
  const active = {};
  const sort = searchParams.get("sort") || "featured";
  const page = Math.max(1, Number(searchParams.get("page") || 1));

  for (const [key, dim] of Object.entries(taxonomy)) {
    if (dim.type === "price") {
      const min = searchParams.get(`${key}_min`);
      const max = searchParams.get(`${key}_max`);
      if (min != null || max != null) {
        active[key] = { min: min != null ? Number(min) : null, max: max != null ? Number(max) : null };
      }
      continue;
    }
    // URL-friendly shortcut for connectivity
    const urlKey = key === "connectivity" ? "connect" : key;
    const raw = searchParams.get(urlKey) ?? searchParams.get(key);
    if (raw) active[key] = new Set(raw.split(",").filter(Boolean));
  }
  return { active, sort, page };
}

export function writeFiltersToUrl(pathname, state, taxonomy) {
  const params = new URLSearchParams();
  for (const [key, dim] of Object.entries(taxonomy)) {
    const v = state.active[key];
    if (!v) continue;
    if (dim.type === "price") {
      if (v.min != null) params.set(`${key}_min`, String(v.min));
      if (v.max != null) params.set(`${key}_max`, String(v.max));
    } else if (v instanceof Set && v.size > 0) {
      const urlKey = key === "connectivity" ? "connect" : key;
      params.set(urlKey, Array.from(v).join(","));
    }
  }
  if (state.sort && state.sort !== "featured") params.set("sort", state.sort);
  if (state.page && state.page > 1) params.set("page", String(state.page));
  const qs = params.toString();
  const url = qs ? `${pathname}?${qs}` : pathname;
  try { window.history.replaceState({}, "", url); } catch {}
  return url;
}

export function countActive(activeFilters, taxonomy) {
  let n = 0;
  for (const [key, dim] of Object.entries(taxonomy)) {
    const v = activeFilters[key];
    if (!v) continue;
    if (dim.type === "price") {
      if (v.min != null || v.max != null) n++;
    } else if (v instanceof Set && v.size > 0) {
      n += v.size;
    }
  }
  return n;
}
